import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/providers/AuthProvider'
import { usePermission } from '@/lib/permissions'

/**
 * Wraps a route element and renders it only when the user holds at least one of
 * `anyOf` (superadmins always pass; an empty list is always allowed). Otherwise
 * redirects to the "no access" page. Sits inside RequireAuth, so the user is
 * already authenticated here — this guards by permission.
 *
 * Checks resolve against the SELECTED TENANT (via usePermission), so switching
 * tenant re-evaluates the guard: the same route can be permitted in one tenant
 * and refused in another.
 */
export function Protected({
  anyOf,
  children,
}: {
  anyOf: readonly string[]
  children: ReactNode
}) {
  const { isAuthenticated, user } = useAuth()
  const { canAny } = usePermission()

  // The principal is resolved asynchronously by AuthProvider's /auth/me call, and
  // `canAny` returns false while `user` is still null. Deciding now would send an
  // authorised user to /no-access purely because that fetch hadn't landed yet —
  // and it never recovers, because the redirect has already happened. Hold the
  // render until the principal is known. Reproduced intermittently under load on
  // hard loads of any guarded route.
  if (isAuthenticated && !user) {
    return null
  }

  if (!canAny(anyOf)) {
    return <Navigate to='/no-access' replace />
  }
  return <>{children}</>
}
